import { supabase } from '../lib/supabase';
import { uploadDocument, downloadDocument } from './storageService';

export interface DocumentGeneration {
  id: string;
  user_id?: string;
  user_session_id?: string;
  user_email?: string;
  document_type: string;
  form_data: Record<string, any>;
  status?: string;
  standard_document?: string;
  enhanced_document?: string;
  premium_document?: string;
  current_tier?: string;
  error_message?: string;
  generation_metadata?: Record<string, any>;
  cache_hit?: boolean;
  generation_time_ms?: number;
  access_tier?: string;
  payment_status?: string;
  sync_status?: string;
  parent_document_id?: string;
  pdf_storage_path?: string;
  property_id?: string;
  organization_id?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface Payment {
  id: string;
  document_generation_id: string;
  stripe_session_id?: string;
  stripe_payment_intent_id?: string;
  amount: number;
  currency: string;
  tier: 'enhanced' | 'premium';
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  customer_email?: string;
  metadata: Record<string, any>;
  created_at: string;
  completed_at?: string;
}

export const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('user_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('user_session_id', sessionId);
  }
  return sessionId;
};

export const saveDocumentGeneration = async (data: {
  documentType: string;
  formData: Record<string, any>;
  standardContent?: string;
  enhancedContent?: string;
  premiumContent?: string;
  standardPath?: string;
  enhancedPath?: string;
  premiumPath?: string;
  userEmail?: string;
  cacheHit?: boolean;
  generationTimeMs?: number;
  metadata?: Record<string, any>;
}): Promise<DocumentGeneration> => {
  const sessionId = getSessionId();

  const insertData: any = {
    user_session_id: sessionId,
    user_email: data.userEmail,
    document_type: data.documentType,
    form_data: data.formData,
    status: 'completed',
    access_tier: 'premium',
    payment_status: 'unpaid',
    cache_hit: data.cacheHit,
    generation_time_ms: data.generationTimeMs,
    generation_metadata: data.metadata,
  };

  if (data.standardContent) {
    insertData.standard_document = data.standardContent;
  }
  if (data.enhancedContent) {
    insertData.enhanced_document = data.enhancedContent;
  }
  if (data.premiumContent) {
    insertData.premium_document = data.premiumContent;
  }

  const { data: document, error } = await supabase
    .from('document_generations')
    .insert(insertData)
    .select()
    .single();

  if (error) {
    console.error('Error saving document:', error);
    throw new Error('Failed to save document generation');
  }

  return document;
};

export const getDocumentGeneration = async (documentId: string): Promise<DocumentGeneration | null> => {
  const { data, error } = await supabase
    .from('document_generations')
    .select('*')
    .eq('id', documentId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching document:', error);
    return null;
  }

  return data;
};

export const updateDocumentAccess = async (
  documentId: string,
  tier: 'enhanced' | 'premium',
  paymentStatus: 'pending' | 'paid'
): Promise<void> => {
  const { error } = await supabase
    .from('document_generations')
    .update({
      access_tier: tier,
      payment_status: paymentStatus,
    })
    .eq('id', documentId);

  if (error) {
    console.error('Error updating document access:', error);
    throw new Error('Failed to update document access');
  }
};

export const createPaymentRecord = async (data: {
  documentGenerationId: string;
  stripeSessionId: string;
  amount: number;
  tier: 'enhanced' | 'premium';
  customerEmail?: string;
}): Promise<Payment> => {
  const { data: payment, error } = await supabase
    .from('payments')
    .insert({
      document_generation_id: data.documentGenerationId,
      stripe_session_id: data.stripeSessionId,
      amount: data.amount,
      currency: 'gbp',
      tier: data.tier,
      status: 'pending',
      customer_email: data.customerEmail,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating payment record:', error);
    throw new Error('Failed to create payment record');
  }

  return payment;
};

export const getPaymentByStripeSession = async (stripeSessionId: string): Promise<Payment | null> => {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('stripe_session_id', stripeSessionId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching payment:', error);
    return null;
  }

  return data;
};

export const getUserDocuments = async (): Promise<DocumentGeneration[]> => {
  const sessionId = getSessionId();

  const { data, error } = await supabase
    .from('document_generations')
    .select('*')
    .eq('user_session_id', sessionId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching user documents:', error);
    return [];
  }

  return data || [];
};

export const getDocumentContent = async (
  document: DocumentGeneration,
  tier: 'standard' | 'enhanced' | 'premium'
): Promise<string | null> => {
  try {
    const pathField = `pdf_storage_path` as keyof DocumentGeneration;
    const contentField = `${tier}_document` as keyof DocumentGeneration;

    const path = document[pathField] as string | undefined;
    if (path) {
      return await downloadDocument(path);
    }

    const content = document[contentField] as string | undefined;
    return content || null;
  } catch (error) {
    console.error(`Error getting ${tier} document content:`, error);
    return null;
  }
};

export const updateDocumentWithUpgradedVersion = async (
  documentId: string,
  tier: 'enhanced' | 'premium',
  content: string,
  path?: string,
  generationTimeMs?: number
): Promise<void> => {
  const updateData: any = {};

  if (path) {
    updateData.pdf_storage_path = path;
  }
  updateData[`${tier}_document`] = content;

  if (generationTimeMs) {
    updateData.generation_time_ms = generationTimeMs;
  }

  const { error } = await supabase
    .from('document_generations')
    .update(updateData)
    .eq('id', documentId);

  if (error) {
    console.error(`Error updating ${tier} document:`, error);
    throw new Error(`Failed to update ${tier} document`);
  }
};
